import { useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, FileText, FolderOpen, Stethoscope, Headphones,
  Briefcase, Video, Settings, AlertCircle, LogOut, ArrowLeft, Mail, BarChart3
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";

const NAV_SECTIONS = [
  {
    title: "Overview",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
      { label: "Analytics", icon: BarChart3, path: "/admin/analytics" },
    ],
  },
  {
    title: "Content",
    items: [
      { label: "Articles", icon: FileText, path: "/admin/articles" },
      { label: "Categories", icon: FolderOpen, path: "/admin/categories" },
      { label: "Doctors", icon: Stethoscope, path: "/admin/doctors" },
    ],
  },
  {
    title: "Media",
    items: [
      { label: "Podcasts", icon: Headphones, path: "/admin/podcasts" },
      { label: "Videos", icon: Video, path: "/admin/videos" },
    ],
  },
  {
    title: "Jobs & News",
    items: [
      { label: "Jobs", icon: Briefcase, path: "/admin/jobs" },
      { label: "Breaking News", icon: AlertCircle, path: "/admin/breaking-news" },
    ],
  },
  {
    title: "Engagement",
    items: [
      { label: "Newsletter", icon: Mail, path: "/admin/newsletter" },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Settings", icon: Settings, path: "/admin/settings" },
    ],
  },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user === false) {
      navigate("/login");
    }
    if (!loading && user && user.role !== "admin") {
      navigate("/");
    }
  }, [user, loading, navigate]);

  if (loading || user === null) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Skeleton className="w-64 min-h-screen" />
        <div className="flex-1 p-8"><Skeleton className="h-96" /></div>
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-60 bg-slate-900 text-slate-300 flex-shrink-0 min-h-screen hidden md:flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-slate-800">
          <Link to="/" className="flex items-center">
            <div className="h-12 w-44 overflow-hidden flex items-center justify-center bg-white/10 rounded">
              <img src="https://customer-assets.emergentagent.com/job_doctor-speak-hub/artifacts/thatxx48_Pulse%20Medical%20Media%20Logo%20-%20Smooth%20Blue%20Design.svg" alt="Pulse Medical Media" className="h-[170px] w-auto" style={{marginTop: '14px'}} />
            </div>
          </Link>
          <p className="text-[10px] font-sans text-slate-500 uppercase tracking-[0.15em] mt-2 pl-1">Admin Panel</p>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV_SECTIONS.map((section, si) => (
            <div key={section.title}>
              {si > 0 && <Separator className="my-2 bg-slate-800" />}
              <p className="text-[10px] font-sans font-bold text-slate-500 uppercase tracking-[0.15em] px-5 py-2">{section.title}</p>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path || (item.path !== "/admin" && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-5 py-2.5 text-sm font-sans transition-colors ${
                      isActive ? "bg-slate-800 text-white border-l-2 border-[#1E88E5]" : "hover:bg-slate-800/50 hover:text-white border-l-2 border-transparent"
                    }`}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-1">
          <Link to="/" className="flex items-center gap-2 text-sm font-sans text-slate-400 hover:text-white transition-colors px-2 py-2" data-testid="admin-back-site">
            <ArrowLeft className="h-4 w-4" /> Back to Site
          </Link>
          <button onClick={logout} className="flex items-center gap-2 text-sm font-sans text-slate-400 hover:text-white transition-colors px-2 py-2 w-full" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white p-4 flex items-center justify-between">
        <Link to="/admin" className="font-serif font-bold text-lg">Pulse CMS</Link>
        <div className="flex gap-3">
          <Link to="/" className="text-xs text-slate-400 hover:text-white font-sans">Site</Link>
          <button onClick={logout} className="text-xs text-slate-400 hover:text-white font-sans">Logout</button>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 p-6 md:p-8 lg:p-10 md:mt-0 mt-16 overflow-auto" data-testid="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
